import i18n from '@i18n';
import { ApiClient } from '@utils/apiClient';
import { handleApiResult } from '@utils/apiErrorHandler';
import {
  FaqEntryInput,
  UnansweredQuestion,
  UnansweredQuestionDetail,
  UnansweredQuestionStatus,
} from '../types';
import { FaqApiError, unwrap } from './faqService';

const BASE_PATH = '/knowledge-base/unanswered-questions';

// Answered, resolved or dismissed meanwhile, e.g. by another admin (409)
export class QuestionAlreadyHandledError extends FaqApiError {}

const request = async <T>(response: Response, fallback: string): Promise<T> => {
  if (response.status === 409) {
    throw new QuestionAlreadyHandledError(i18n.t('knowledgeBase.unanswered.alreadyHandled'));
  }
  return unwrap<T>(await handleApiResult<T>(response, fallback), fallback) as T;
};

export const unansweredQuestionsService = {
  list: async (
    knowledgeBaseId: string,
    status: UnansweredQuestionStatus,
    search = ''
  ): Promise<UnansweredQuestion[]> => {
    const params = new URLSearchParams({ knowledge_base: knowledgeBaseId, status });
    if (search) params.set('search', search);
    const data = await request<UnansweredQuestion[]>(
      await ApiClient.get(`${BASE_PATH}/?${params.toString()}`),
      'Failed to load unanswered questions'
    );
    return data ?? [];
  },
  get: async (id: string): Promise<UnansweredQuestionDetail> =>
    request<UnansweredQuestionDetail>(
      await ApiClient.get(`${BASE_PATH}/${id}/`),
      'Failed to load unanswered question'
    ),
  // Creates a FAQ entry and resolves the question with it
  answer: async (id: string, data: FaqEntryInput): Promise<UnansweredQuestion> =>
    request<UnansweredQuestion>(
      await ApiClient.post(`${BASE_PATH}/${id}/answer/`, data),
      'Failed to save the answer'
    ),
  resolve: async (id: string): Promise<UnansweredQuestion> =>
    request<UnansweredQuestion>(
      await ApiClient.post(`${BASE_PATH}/${id}/resolve/`, {}),
      'Failed to resolve the question'
    ),
  dismiss: async (id: string): Promise<UnansweredQuestion> =>
    request<UnansweredQuestion>(
      await ApiClient.post(`${BASE_PATH}/${id}/dismiss/`, {}),
      'Failed to dismiss the question'
    ),
};
